import { useState, useMemo } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Input,
  Button,
  InputGroup,
  Badge,
  Alert,
  FormGroup,
  Label,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useNexusSettings, useSetNexusSetting } from './useNexusSettings';

/**
 * Nexus System-Settings (Spec 3 von 8).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-system-settings.md
 * Endpoints: /v1/admin/settings (Allowlist im Backend)
 *
 * Settings sind Schema-validiert serverseitig — UI bietet Type-spezifische
 * Inputs (string/url/csv/integer/boolean) plus generischen Free-Text-Fallback.
 */
export function NexusSettingsPage() {
  const query = useNexusSettings();
  const save = useSetNexusSetting();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  const settings = query.data?.settings ?? [];
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of settings) {
      const g = s.group ?? 'Sonstiges';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(s);
    }
    return Array.from(map.entries());
  }, [settings]);

  if (query.isLoading) {
    return (
      <div>
        <PageHeader title="Nexus · System-Settings" />
        <QueryLoadingState />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div>
        <PageHeader title="Nexus · System-Settings" />
        <QueryErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Nexus · System-Settings"
        subtitle="Allowlist-Settings · GET /v1/admin/settings (Backend persistiert in app_settings)"
      />

      <Alert color="info" className="small">
        Nur diese Settings sind Allowlist-freigeschaltet — Secrets bleiben in
        Server-ENV (siehe Spec § "Strikt im ENV"). Nach jedem PUT publisht
        Nexus <code>nexus.settings.invalidate</code> → 60s-Cache wird gedroppt.
      </Alert>

      {groups.map(([groupName, items]) => (
        <Card key={groupName} className="mb-3">
          <CardBody>
            <CardTitle tag="h6" className="text-uppercase small text-muted mb-3">
              {groupName}
            </CardTitle>
            {items.map((setting) => (
              <SettingRow
                key={`${setting.key}-${setting.updated_at ?? ''}`}
                setting={setting}
                canWrite={canWrite}
                onSave={(value) => save.mutateAsync({ key: setting.key, value })}
                busy={save.isPending}
              />
            ))}
          </CardBody>
        </Card>
      ))}

      {!canWrite && (
        <Alert color="secondary" className="small">
          Nur-Lese-Modus — Permission <code>integrations.write</code> fehlt.
        </Alert>
      )}
    </div>
  );
}

function SettingRow({ setting, canWrite, onSave, busy }) {
  // Key-prop-Remount-Pattern in der Parent stellt sicher dass eine neue
  // setting.value-Quelle eine neue Row-Instanz mit frischem State liefert
  // — siehe NexusSettingsPage. Damit reicht der einmalige Initial-State.
  const [draft, setDraft] = useState(setting.value);
  const [error, setError] = useState(null);

  const dirty = JSON.stringify(draft) !== JSON.stringify(setting.value);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await onSave(coerceForType(draft, setting.type));
    } catch (err) {
      setError(err?.data?.message ?? err?.message ?? 'Speichern fehlgeschlagen.');
    }
  };

  return (
    <FormGroup className="mb-3 pb-3 border-bottom">
      <Label className="d-flex justify-content-between align-items-start mb-1">
        <span className="fw-semibold">
          <code className="small">{setting.key}</code>
          {dirty && <Badge color="warning" pill className="ms-2">geändert</Badge>}
        </span>
        <small className="text-muted">
          {setting.type}
          {setting.updated_at && (
            <> · <RelativeTime iso={setting.updated_at} /></>
          )}
        </small>
      </Label>
      <form onSubmit={onSubmit}>
        <InputGroup size="sm">
          <SettingInput
            type={setting.type}
            value={draft}
            onChange={setDraft}
            disabled={!canWrite || busy}
          />
          <HasPermission perms={['integrations.write']}>
            <Button
              type="submit"
              color="primary"
              outline
              disabled={!dirty || busy}
            >
              {busy ? 'Speichert…' : 'Speichern'}
            </Button>
            {dirty && (
              <Button
                type="button"
                color="secondary"
                outline
                onClick={() => setDraft(setting.value)}
                disabled={busy}
              >
                Verwerfen
              </Button>
            )}
          </HasPermission>
        </InputGroup>
        {error && <div className="small text-danger mt-1">{error}</div>}
      </form>
    </FormGroup>
  );
}

function SettingInput({ type, value, onChange, disabled }) {
  if (type === 'boolean') {
    return (
      <div className="form-check form-switch ms-2 flex-grow-1">
        <Input
          type="switch"
          role="switch"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
      </div>
    );
  }
  if (type === 'integer') {
    return (
      <Input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        disabled={disabled}
        className="font-monospace"
      />
    );
  }
  // string, url, csv → text
  return (
    <Input
      type={type === 'url' ? 'url' : 'text'}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={type === 'csv' || type === 'string' ? 'font-monospace' : undefined}
    />
  );
}

function coerceForType(value, type) {
  if (type === 'boolean') return !!value;
  if (type === 'integer') return value == null || value === '' ? null : Number(value);
  return value;
}
